import React, { useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';
import { API_ENDPOINTS } from '../config/api.config';

interface CompanyData {
  companyName: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  businessHours?: {
    [key: string]: {
      open?: string;
      close?: string;
      closed?: boolean;
    };
  };
}

const Returns: React.FC = () => {
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCompanyInfo = async () => {
      try {
        const data = await apiRequest<CompanyData>(API_ENDPOINTS.COMPANY.PUBLIC);
        setCompanyData(data);
      } catch (error) {
        console.error('Error loading company info:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCompanyInfo();
  }, []);
  return (
    <div className="bg-neutral-50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
              Returns & Refunds
            </h1>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Your satisfaction is our priority. Learn about our return and refund policies.
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
            {/* Important Notice */}
            <section className="bg-red-50 border border-red-200 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-red-800 mb-4 flex items-center">
                <span className="mr-3">⚠️</span>
                Important Notice for Fireworks
              </h2>
              <div className="text-red-700 space-y-2">
                <p className="font-semibold">Due to safety regulations and the nature of fireworks products:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Returns are only accepted for unopened, unused products</li>
                  <li>Items must be in original packaging with all safety seals intact</li>
                  <li>No returns accepted for used or opened fireworks</li>
                  <li>Special handling required for all return shipments</li>
                </ul>
              </div>
            </section>

            {/* Return Policy */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">🔄</span>
                Return Policy
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-3">Returnable Items</h3>
                    <ul className="space-y-2 text-neutral-600">
                      <li>• Unopened fireworks in original packaging</li>
                      <li>• Defective products (manufacturing defects)</li>
                      <li>• Incorrect items received</li>
                      <li>• Damaged during shipping</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-3">Return Window</h3>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-green-800 font-semibold">7 Days</p>
                      <p className="text-green-700 text-sm">From delivery date for eligible returns</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-3">Non-Returnable Items</h3>
                    <ul className="space-y-2 text-neutral-600">
                      <li>• Used or opened fireworks</li>
                      <li>• Items without original packaging</li>
                      <li>• Custom or personalized orders</li>
                      <li>• Items ordered during sale periods (final sale)</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-3">Return Condition</h3>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-blue-800 font-semibold">Original Condition</p>
                      <p className="text-blue-700 text-sm">Unopened with all seals and labels intact</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Return Process */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">📋</span>
                How to Return
              </h2>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="text-center p-6 bg-neutral-50 rounded-xl">
                  <div className="text-3xl mb-3">1️⃣</div>
                  <h3 className="font-semibold text-neutral-900 mb-2">Contact Us</h3>
                  <p className="text-sm text-neutral-600">Call or email within 7 days of delivery</p>
                </div>
                <div className="text-center p-6 bg-neutral-50 rounded-xl">
                  <div className="text-3xl mb-3">2️⃣</div>
                  <h3 className="font-semibold text-neutral-900 mb-2">Get Authorization</h3>
                  <p className="text-sm text-neutral-600">Receive return authorization number</p>
                </div>
                <div className="text-center p-6 bg-neutral-50 rounded-xl">
                  <div className="text-3xl mb-3">3️⃣</div>
                  <h3 className="font-semibold text-neutral-900 mb-2">Pack Securely</h3>
                  <p className="text-sm text-neutral-600">Use original packaging and safety materials</p>
                </div>
                <div className="text-center p-6 bg-neutral-50 rounded-xl">
                  <div className="text-3xl mb-3">4️⃣</div>
                  <h3 className="font-semibold text-neutral-900 mb-2">Ship Back</h3>
                  <p className="text-sm text-neutral-600">Use our prepaid return label</p>
                </div>
              </div>
            </section>

            {/* Refund Policy */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">💰</span>
                Refund Policy
              </h2>
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-green-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-green-800 mb-3">Full Refund</h3>
                    <ul className="space-y-2 text-green-700">
                      <li>• Defective products</li>
                      <li>• Wrong items shipped</li>
                      <li>• Damaged during shipping</li>
                      <li>• Order cancellation before dispatch</li>
                    </ul>
                  </div>
                  <div className="bg-yellow-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-yellow-800 mb-3">Partial Refund</h3>
                    <ul className="space-y-2 text-yellow-700">
                      <li>• Return shipping costs deducted</li>
                      <li>• Restocking fee (if applicable)</li>
                      <li>• Processing charges (if any)</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-neutral-100 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-3">Refund Timeline</h3>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <p className="font-semibold text-accent-600">3-5 Business Days</p>
                      <p className="text-sm text-neutral-600">Processing after item received</p>
                    </div>
                    <div>
                      <p className="font-semibold text-accent-600">5-7 Business Days</p>
                      <p className="text-sm text-neutral-600">Bank/Card processing time</p>
                    </div>
                    <div>
                      <p className="font-semibold text-accent-600">Total: 8-12 Days</p>
                      <p className="text-sm text-neutral-600">Complete refund timeline</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Exchanges */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">🔄</span>
                Exchanges
              </h2>
              <div className="bg-blue-50 rounded-xl p-6">
                <p className="text-blue-800 mb-4">
                  We offer exchanges for defective items or if you received the wrong product. 
                  Due to safety regulations, we cannot exchange fireworks for different types or quantities.
                </p>
                <h3 className="font-semibold text-blue-800 mb-2">Exchange Process:</h3>
                <ul className="space-y-1 text-blue-700">
                  <li>• Same process as returns</li>
                  <li>• New item ships after we receive return</li>
                  <li>• No additional shipping charges for defective items</li>
                  <li>• Priority processing for exchanges</li>
                </ul>
              </div>
            </section>

            {/* Contact Information */}
            <section className="bg-accent-50 rounded-xl p-6">
              <h2 className="text-xl font-bold text-neutral-900 mb-4">Return Support</h2>
              <p className="text-neutral-700 mb-4">
                Need help with a return? Our customer service team is here to assist you.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-neutral-900 mb-2">Contact Information</h3>
                  {loading ? (
                    <div className="text-neutral-600">Loading...</div>
                  ) : companyData ? (
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <span className="mr-2">📞</span>
                        <span className="text-neutral-700">{companyData.phone}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="mr-2">✉️</span>
                        <span className="text-neutral-700">{companyData.email}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-neutral-600">Contact information unavailable</div>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-900 mb-2">Business Hours</h3>
                  {loading ? (
                    <div className="text-neutral-600">Loading...</div>
                  ) : companyData?.businessHours ? (
                    <div className="space-y-1 text-neutral-700">
                      {Object.entries(companyData.businessHours).map(([day, hours]) => {
                        const dayCapitalized = day.charAt(0).toUpperCase() + day.slice(1);
                        if (hours.closed) {
                          return <p key={day}>{dayCapitalized}: Closed</p>;
                        }
                        return <p key={day}>{dayCapitalized}: {hours.open} - {hours.close}</p>;
                      })}
                    </div>
                  ) : (
                    <div className="space-y-1 text-neutral-700">
                      <p>Monday - Friday: 9 AM - 7 PM</p>
                      <p>Saturday: 10 AM - 6 PM</p>
                      <p>Sunday: 10 AM - 4 PM</p>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Returns;